import { DateTime } from "luxon";
import {
  Fragment,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import Helmet from "react-helmet";
import { Link, useParams } from "react-router-dom";

import { ApiService, LISCase, LISEvent } from "pwapi";

import { ApiError } from "pwapi/core/ApiError";

import { useIsLoading } from "shared/hooks/requestStatusHooks";

import { getCase } from "app/actions/caseActions";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { useSelectedLabStation } from "hooks/useSelectedLabStation";
import CaseHierarchy from "ui/accessioning/CaseHierarchy";
import CaseWorkHierarchy from "ui/accessioning/CaseWorkHierarchy";
import AccessionNumber from "ui/components/AccessionNumber";
import AppError from "ui/components/AppError";
import AppSpinner from "ui/components/AppSpinner";

type FullEvent = LISEvent & {
  specimen_name: string;
  block_name: string;
  slide_number: string;
  stain_abbreviation: string;
  task_type: string;
  task_label: string;
  order_id: string | number;
  record_type: string;
  record_id?: number;
  record_name: string;
};

export const mapCaseHistoryErrorStatusToMessage: { [key: number]: string } = {
  404: "No Case History was found for the selected Lab and Station.",
};

const EventRow = ({ event }: { event: FullEvent }) => {
  const labStation = useAppSelector(
    (state) => state.labStations[event.lab_station_id || 0]
  );

  return (
    <Fragment>
      <div className="grid-cell">{event.record_type}</div>
      <div className="grid-cell">
        {event.record_type && event.record_id ? (
          <a href={`#${event.record_type}-${event.record_id}`}>
            {event.record_name}
          </a>
        ) : (
          event.record_name
        )}
      </div>
      <div className="grid-cell">{event.event}</div>
      <div className="grid-cell">
        {!!event.created_at &&
          DateTime.fromISO(event.created_at).toLocaleString(
            DateTime.DATETIME_SHORT_WITH_SECONDS
          )}
      </div>
      <div className="grid-cell">{labStation?.name}</div>
      <div className="grid-cell">
        {!!event.order_id && (
          <a href={`#Order-${event.order_id}`}>{event.order_id}</a>
        )}
      </div>
      <div className="grid-cell">
        {!!event.work_id && (
          <a href={`#Work-${event.work_id}`}>{event.work_id}</a>
        )}
      </div>
      <div className="grid-cell">
        {!!event.task_id && (
          <a href={`#Task-${event.task_id}`}>{event.task_label}</a>
        )}
      </div>
      <div className="grid-cell">
        {event.user
          ? [event.user.first_name, event.user.last_name]
              .filter((s) => !!s)
              .join(" ")
          : event.user_id || "AUTOMATED"}
      </div>
    </Fragment>
  );
};

type EventsTableFilters = {
  Specimen: number[];
  Block: number[];
  Slide: number[];
  Order: number[];
  Work: number[];
  Task: number[];
};

const defaultFilters: EventsTableFilters = {
  Specimen: [],
  Block: [],
  Slide: [],
  Order: [],
  Work: [],
  Task: [],
};

const EventsTableFilterButton = ({
  onClickFilter,
  type,
  id,
}: {
  onClickFilter: (
    event: MouseEvent<HTMLElement>,
    type: keyof EventsTableFilters,
    id: number
  ) => void;
  type: keyof EventsTableFilters;
  id: number;
}) => {
  const slide = useAppSelector((state) =>
    state.case?.slides?.find(
      (slide) => slide.id === (type === "Slide" ? id : 0)
    )
  );
  const stain = useAppSelector((state) => state.stains[slide?.stain_id || 0]);
  const block = useAppSelector((state) =>
    state.case?.blocks?.find(
      (block) => block.id === (type === "Block" ? id : slide?.block_obj_id)
    )
  );
  const specimen = useAppSelector((state) =>
    state.case?.specimens?.find(
      (specimen) =>
        specimen.id === (type === "Specimen" ? id : block?.specimen_id)
    )
  );

  return (
    <button
      className="EventsTable--filters--filter"
      key={`${type}-${id}`}
      onClick={(event: MouseEvent<HTMLElement>) => {
        event.altKey = true;
        onClickFilter(event, type, id);
      }}
    >
      {type}{" "}
      {type === "Specimen"
        ? specimen?.name
        : type === "Block"
        ? `${specimen?.name || ""}${block?.name || ""}`
        : type === "Slide"
        ? `${specimen?.name || ""}${block?.name || ""} ${
            stain?.abbreviation
          } S${slide?.slide_number}`
        : id}{" "}
      &#10005;
    </button>
  );
};

const EventsTable = ({
  pathCase,
  onClickFilter,
  events,
  filters = defaultFilters,
}: {
  pathCase: LISCase;
  onClickFilter: (
    event: MouseEvent<HTMLElement>,
    type: keyof EventsTableFilters,
    id: number
  ) => void;
  events: LISEvent[];
  filters: EventsTableFilters;
}) => {
  const stains = useAppSelector((state) => state.stains);

  const filterKeys = (
    Object.keys(filters) as (keyof EventsTableFilters)[]
  ).filter((filterKey) => !!filters && !!filters[filterKey].length);
  const totalFilters = Object.values(filters).reduce(
    (total, ids) => total + ids.length,
    0
  );

  const fullEvents = useMemo(
    () =>
      events
        .filter((event) => {
          if (!totalFilters) return true;
          if (event.specimen_id && filters.Specimen.includes(event.specimen_id))
            return true;
          if (event.block_id && filters.Block.includes(event.block_id))
            return true;
          if (event.slide_id && filters.Slide.includes(event.slide_id))
            return true;
          const order_id = pathCase.work_processes.find(
            (work) => work.id === event.work_id
          )?.order?.id;
          if (order_id && filters.Order.includes(order_id)) return true;
          if (event.work_id && filters.Work.includes(event.work_id))
            return true;
          if (event.task_id && filters.Task.includes(event.task_id))
            return true;
          return false;
        })
        .map((event): FullEvent => {
          // if (!pathCase || !events) return [];

          const slide = pathCase.slides.find(
            (slide) => slide.id === event.slide_id
          );
          const stain = stains[slide?.stain_id || 0];
          const block = pathCase.blocks.find(
            (block) =>
              block.id === slide?.block_obj_id || block.id === event.block_id
          );
          const specimen = pathCase.specimens.find(
            (specimen) =>
              specimen.id === block?.specimen_id ||
              specimen.id === event.specimen_id
          );

          const work = pathCase.work_processes.find(
            (work) => work.id === event.work_id
          );
          const task = work?.tasks.find((task) => task.id === event.task_id);

          const record_type = event.slide_id
            ? "Slide"
            : event.block_id
            ? "Block"
            : event.specimen_id
            ? "Specimen"
            : "-";
          const record_id =
            event.slide_id || event.block_id || event.specimen_id;

          let record_name = "";
          if (["Specimen", "Block", "Slide"].includes(record_type)) {
            record_name += specimen?.name || "";

            if (["Block", "Slide"].includes(record_type)) {
              record_name += block?.name || "";

              if (record_type === "Slide") {
                record_name += ` ${stain?.abbreviation || ""} S${
                  slide?.slide_number || ""
                }`;
              }
            }
          }

          return {
            ...event,
            specimen_name: specimen ? specimen.name : "-",
            block_name: block?.name || "-",
            slide_number: slide ? `S${slide.slide_number}` : "-",
            stain_abbreviation: stain?.abbreviation,
            task_type: task?.type || "",
            task_label: task ? `${task.id} (${task.type.toUpperCase()})` : "",
            order_id: work?.order?.id || "-",
            record_type,
            record_id,
            record_name,
          };
        })
        .sort((event) => (event.id || 0) * -1),
    [events, pathCase, stains, filters, totalFilters]
  );

  return (
    <div className="EventsTable">
      {!!totalFilters && (
        <div className="EventsTable--filters">
          Filter{totalFilters > 1 && "s"}:
          {filterKeys.flatMap((filterKey) =>
            filters[filterKey].map((id) => (
              <EventsTableFilterButton
                type={filterKey}
                id={id}
                key={`${filterKey}-${id}`}
                onClickFilter={onClickFilter}
              />
            ))
          )}
        </div>
      )}
      <div className="events-grid">
        <div className="grid-header">Type</div>
        <div className="grid-header">Name</div>
        <div className="grid-header">Event</div>
        <div className="grid-header">
          Timestamp <sup>&#8595;</sup>
        </div>
        <div className="grid-header">Station</div>
        <div className="grid-header">Order</div>
        <div className="grid-header">Work</div>
        <div className="grid-header">Task</div>
        <div className="grid-header">User</div>
        {fullEvents.map((event) => (
          <EventRow key={event.id} event={event} />
        ))}
      </div>
    </div>
  );
};

const CaseHistory = () => {
  const dispatch = useAppDispatch();
  const selectedLabStationId = useSelectedLabStation().id;
  const case_id = Number(useParams().case_id);
  const pathCase = useAppSelector((state) => state.case);
  const loadingCase = useIsLoading(getCase, {
    id: case_id,
    parentLookupLabStationId: selectedLabStationId,
  });
  const [events, setEvents] = useState<LISEvent[]>([]);
  const [filters, setFilters] = useState<EventsTableFilters>(defaultFilters);

  const onClickFilter = useCallback(
    (
      event: MouseEvent<HTMLElement>,
      type: keyof EventsTableFilters,
      id: number
    ) => {
      event.preventDefault();

      if (event.altKey) {
        // Remove filter if it is selected
        if (filters[type].includes(id)) {
          const { [filters[type].indexOf(id)]: _, ...rest } = filters[type];
          setFilters({
            ...filters,
            [type]: Object.values(rest),
          });
        }
      } else if (event.shiftKey) {
        // Add filter if it isn't already selected
        if (!filters[type].includes(id)) {
          setFilters({
            ...filters,
            [type]: [...filters[type], id],
          });
        }
      } else {
        // Toggle between just this filter and no filters
        const totalFilters = Object.values(filters).reduce(
          (total, ids) => total + ids.length,
          0
        );
        setFilters({
          ...defaultFilters,
          [type]: totalFilters === 1 && filters[type].includes(id) ? [] : [id],
        });
      }
    },
    [filters]
  );

  const [error, setError] = useState<string>();
  const [caseError, setCaseError] = useState<boolean>(false);

  useEffect(() => {
    if (!pathCase || (pathCase && pathCase?.id !== case_id)) {
      dispatch(
        getCase({
          id: case_id,
          parentLookupLabStationId: selectedLabStationId,
        })
      ).catch(() => {
        setCaseError(true);
      });
    }
  }, [case_id]);

  useEffect(() => {
    if (!pathCase?.id) return;

    if (!selectedLabStationId) {
      setError("Please select a lab station to view case history");
      return;
    }

    ApiService.historyCaseWithWork({
      parentLookupLabStationId: selectedLabStationId,
      id: case_id,
    })
      .then((res) => {
        setEvents(res);
      })
      .catch((err: ApiError) => {
        const errorText =
          mapCaseHistoryErrorStatusToMessage[err.status] ||
          `${err.status} - ${err.message}`;

        !error && setError(errorText);
      });
  }, [pathCase?.id, selectedLabStationId]);

  if (caseError) {
    return (
      <AppError
        supportText={<>You may not have permission to view this case</>}
        emailSupportText="If you believe you are seeing this message in error"
      >
        404 case not found
      </AppError>
    );
  }

  if (pathCase?.id !== case_id || loadingCase)
    return <AppSpinner>Loading Case...</AppSpinner>;

  return (
    <div className="CaseWrapper CaseHistory">
      <Helmet>
        <title>{`Case ${pathCase?.id} History`}</title>
      </Helmet>

      <header className="Case__header">
        <figure className="Logo">
          <Link to="/cases">
            <img src="/logo.svg" alt="Back to case list" />
          </Link>
        </figure>
        {pathCase?.accession && (
          <AccessionNumber accession={pathCase?.accession} />
        )}
        {pathCase?.patient?.last_name}, {pathCase?.patient?.first_name}
        <span>
          ({pathCase?.patient?.sex} &ndash; {pathCase?.patient?.date_of_birth})
        </span>
        <Link to=".." className="Case__header__edit">
          Back
        </Link>
      </header>
      <div className="columns">
        <div>
          <div>
            <h1>Case Hierarchy</h1>
            <CaseHierarchy
              pathCase={pathCase}
              onClickFilter={onClickFilter}
              showAdminLinks
            />
          </div>
          <div>
            <h1>Work, Orders, &amp; Tasks</h1>
            <CaseWorkHierarchy
              pathCase={pathCase}
              onClickFilter={onClickFilter}
            />
          </div>
        </div>
        <div>
          <h1>Events</h1>

          {!!error ? (
            <div className="CaseHistory__error">{error}</div>
          ) : (
            <EventsTable
              pathCase={pathCase}
              onClickFilter={onClickFilter}
              events={events}
              filters={filters}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CaseHistory;
